import * as React from 'react';
import styled, { withTheme } from 'styled-components';
import { rem } from 'polished';
import { useState } from 'react';
import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { FlexRowCenterVertical, TextP } from '../../..';
import { Input } from '../General/Input';
import { __ } from 'react-i18n';
import { FilterType } from './CategoryFilter';

interface Props {
  theme: any;
  isOpen: boolean;
  filterData: {
    min: number;
    max: number;
    activeMin: string;
    activeMax: string;
    attribId?: number;
  };
  handleFilterChange: (
    type: FilterType,
    newData: { min: number; max: number; attribId?: number },
  ) => void;
  unit?: string;
  activeValues?: any;
  isMobile: boolean;
}

function RangeFilter({
  theme,
  isOpen,
  filterData: { min, max, activeMin, activeMax, attribId },
  handleFilterChange,
  unit,
  activeValues,
  isMobile = false,
}: Props) {
  if (!max || (!min && min !== 0)) {
    return null;
  }
  const correctMin = parseInt(Math.floor(min).toFixed(0), 10);
  const correctMax = parseInt(Math.ceil(max).toFixed(0), 10);
  const initialMin = activeMin
    ? parseInt(activeMin, 10).toFixed(0)
    : min.toFixed(0);
  const initialMax = activeMax
    ? parseInt(activeMax, 10).toFixed(0)
    : correctMax.toFixed(0);
  const [minValue, setMinValue] = useState(parseInt(initialMin, 10));
  const [maxValue, setMaxValue] = useState(parseInt(initialMax, 10));

  const [inputMin, setInputMin] = useState(initialMin);
  const [inputMax, setInputMax] = useState(initialMax);

  React.useEffect(() => {
    const correctInitialMin = activeMin
      ? parseInt(activeMin, 10).toFixed(0)
      : min.toFixed(0);
    const correctInitialMax = activeMax
      ? parseInt(activeMax, 10).toFixed(0)
      : correctMax.toFixed(0);

    setMinValue(parseInt(correctInitialMin, 10));
    setMaxValue(parseInt(correctInitialMax, 10));
    setInputMin(correctInitialMin);
    setInputMax(correctInitialMax);
  }, [activeMin, activeMax, min, correctMax]);

  const defaultValue = [min, correctMax];
  const sliderValue = [minValue, maxValue];

  const handleRangeChange = value => {
    setMinValue(value[0]);
    setMaxValue(value[1]);
    setInputMin(value[0]);
    setInputMax(value[1]);
  };

  const handleMinChange = e => {
    setInputMin(e.target.value);
  };

  const handleMaxChange = e => {
    setInputMax(e.target.value);
  };

  const handleMinBlur = e => {
    let value = e.target.value;
    if (isNaN(value)) {
      value = initialMin;
    } else if (value < min) {
      value = min.toFixed(0);
    } else if (value > correctMax) {
      value = correctMax.toFixed(0);
    } else {
      value = parseInt(value, 10).toFixed(0);
    }

    if (value !== initialMin) {
      setMinValue(value);
      setInputMin(value);
      handleFilterChange(FilterType.RANGE, {
        min: parseInt(value, 10),
        max: maxValue,
        attribId,
      });
    } else {
      setInputMin(value);
    }
  };

  const handleMaxBlur = e => {
    let value = e.target.value;
    if (isNaN(value)) {
      value = initialMax;
    } else if (value > max) {
      value = correctMax.toFixed(0);
    } else if (value < min) {
      value = min.toFixed(0);
    } else {
      value = parseInt(value, 10).toFixed(0);
    }

    if (value !== initialMax) {
      setInputMax(value);
      setMaxValue(value);
      handleFilterChange(FilterType.RANGE, {
        min: minValue,
        max: parseInt(value, 10),
        attribId,
      });
    } else {
      setInputMax(value);
    }
  };

  const handleAfterRangeChange = () => {
    if (
      activeMin !== minValue.toFixed(0) ||
      activeMax !== maxValue.toFixed(0)
    ) {
      handleFilterChange(FilterType.RANGE, {
        min: minValue,
        max: maxValue,
        attribId,
      });
      setInputMin(minValue.toFixed(0));
      setInputMax(maxValue.toFixed(0));
    }
  };

  // isMobile : if true => show version for mobile menu, if false => show version for desktop
  return (
    <>
      {isMobile && isOpen && (
        <PriceFilterDropdown>
          <Range
            className="rc-slider-custom"
            style={{ maxWidth: `250px`, marginLeft: `8px` }}
            min={correctMin}
            max={correctMax}
            allowCross={false}
            value={sliderValue}
            defaultValue={defaultValue}
            onChange={handleRangeChange}
            onAfterChange={handleAfterRangeChange}
            handleStyle={[
              // first track style
              {
                backgroundColor: theme.categoryFilter.trackBgColor,
                border: theme.categoryFilter.trackBorder,
                borderColor: theme.categoryFilter.trackBorderColor,
                width: rem(16),
                height: rem(16),
              },
              // second track style
              {
                backgroundColor: theme.categoryFilter.trackBgColor,
                border: theme.categoryFilter.trackBorder,
                borderColor: theme.categoryFilter.trackBorderColor,
                width: rem(16),
                height: rem(16),
              },
            ]}
            trackStyle={[
              { backgroundColor: theme.categoryFilter.sliderTrackColor },
            ]}
            railStyle={{
              backgroundColor: theme.categoryFilter.sliderRailColor,
            }}
          />
          <InputRow isMobile={isMobile}>
            <PriceLimitInput
              type="number"
              value={inputMin}
              onChange={handleMinChange}
              onBlur={handleMinBlur}
              isMobile={isMobile}
            />
            <BaseTxt style={{ marginRight: 16, marginLeft: 16 }}>
              {__('do')}
            </BaseTxt>
            <PriceLimitInput
              type="number"
              value={inputMax}
              onChange={handleMaxChange}
              onBlur={handleMaxBlur}
              isMobile={isMobile}
            />
            {unit ? <BaseTxt style={{ marginLeft: 16 }}>{unit}</BaseTxt> : null}
          </InputRow>
        </PriceFilterDropdown>
      )}
      {!isMobile && isOpen && (
        <DesktopPriceFilterDropdown>
          <InputRow isMobile={isMobile}>
            <PriceLimitInput
              type="number"
              value={inputMin}
              onChange={handleMinChange}
              onBlur={handleMinBlur}
              isMobile={isMobile}
            />
            <RangeWrapper>
              <Range
                className="rc-slider-custom"
                min={correctMin}
                max={correctMax}
                allowCross={false}
                value={sliderValue}
                defaultValue={defaultValue}
                onChange={handleRangeChange}
                onAfterChange={handleAfterRangeChange}
                handleStyle={[
                  // first track style
                  {
                    backgroundColor: theme.categoryFilter.trackBgColor,
                    border: theme.categoryFilter.trackBorder,
                    borderColor: theme.categoryFilter.trackBorderColor,
                    width: rem(16),
                    height: rem(16),
                  },
                  // second track style
                  {
                    backgroundColor: theme.categoryFilter.trackBgColor,
                    border: theme.categoryFilter.trackBorder,
                    borderColor: theme.categoryFilter.trackBorderColor,
                    width: rem(16),
                    height: rem(16),
                  },
                ]}
                trackStyle={[
                  { backgroundColor: theme.categoryFilter.sliderTrackColor },
                ]}
                railStyle={{
                  backgroundColor: theme.categoryFilter.sliderRailColor,
                }}
              />
            </RangeWrapper>
            <PriceLimitInput
              type="number"
              value={inputMax}
              onChange={handleMaxChange}
              onBlur={handleMaxBlur}
              isMobile={isMobile}
            />
            {unit ? <BaseTxt style={{ marginLeft: 16 }}>{unit}</BaseTxt> : null}
          </InputRow>
        </DesktopPriceFilterDropdown>
      )}
    </>
  );
}

const BaseTxt = styled(TextP)`
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 400;
`;

const FilterDropdown = styled.div`
  /* margin-top: ${rem(32)}; */
`;

const PriceFilterDropdown = styled(FilterDropdown)`
  width: 100%;
  padding-left: ${rem(16)};
  padding-right: ${rem(16)};
`;

const DesktopPriceFilterDropdown = styled(FilterDropdown)`
  margin: 0;
  ${({ theme }) => theme.mediab.m580`
      display: none;
  `}
`;

const InputRow = styled(FlexRowCenterVertical)<{ isMobile: boolean }>`
  margin: ${props => (props.isMobile ? `${rem(32)} auto 0` : '0')};
  width: ${props => (props.isMobile ? 'max-content' : '')};

  margin-left: 0;
  margin-right: 0;

  ${({ theme }) => theme.mediab.s450`
    margin-left: 0;
    margin-right: 0;
    margin-top: ${rem(26)};
    width: 100%;
    margin-bottom: ${rem(32)};
  `}
`;

const PriceLimitInput = styled(Input)<{ isMobile: boolean }>`
  box-shadow: none;
  border: ${rem(1)} solid #cccccc;
  border-radius: ${rem(4)};
  width: ${props => (props.isMobile ? `${rem(96)}` : `${rem(80)}`)};
  padding: 0 ${rem(16)};
  text-align: center;
  color: ${({ theme }) => theme.colors.black};
  font-family: ${({ theme }) => theme.font.primary};
  box-shadow: inset 0px 2px 4px #00000029;
  &:first-of-type {
    margin-right: ${props =>
      props.isMobile ? '0 !important' : `${rem(16)} !important`};
  }

  &:last-of-type {
    margin-left: ${props =>
      props.isMobile ? '0 !important' : `${rem(16)} !important`};
  }

  /* Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }

  ${({ theme }) => theme.mediab.l1150`
    width: ${props => (props.isMobile ? `${rem(96)}` : `${rem(60)}`)};
  `}
`;

const RangeWrapper = styled.div`
  min-width: ${rem(220)};

  ${({ theme }) => theme.mediab.l1150`
    min-width: ${rem(120)};
  `}

  ${({ theme }) => theme.mediab.s450`
    margin-top: 0;
  `}
`;

const WithTheme: any = withTheme(RangeFilter);
export { WithTheme as SanitasRangeFilter };
